import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { customerGrossProfitAccessGuard } from './guards/customer-gross-profit-access.guard';
import { requiresFunction, settingsPageView } from './guards/requires-function.guard';

/**
 * Auth contract: `authGuard` boots `AuthTokenService` — JWT from `?token=` (referrer rules in
 * UAT/prod) or localStorage; no live JWT redirects to the legacy login with a `returnUrl` (see
 * `authGuard` + `SessionRedirectService`). On top of that, the user-group permission system gates
 * routes: `requiresFunction('<area>.view')` for the main areas and `settingsPageView` (code
 * derived from the route slug: `settings/<slug>` → `settings.<slug>.view`) for settings pages.
 * Distant vendor approval/decline pages are NOT protected — they are accessed via email links.
 */

/**
 * Application routes.
 * Job-scoped pages (assign-vendor, estimates, notes-activity, vendor-bills) are lazy-loaded.
 */
export const routes: Routes = [
  {
    // Job Story Library: its own sign-in, session and shell. Deliberately NOT behind `authGuard`.
    path: 'library',
    loadChildren: () => import('./features/library/library.routes').then((m) => m.LIBRARY_ROUTES),
  },
  {
    // Vendor "Show us the win" page (emailed link, no sign-in). Deliberately NOT behind `authGuard`.
    path: 'story',
    loadChildren: () => import('./features/story/story.routes').then((m) => m.STORY_ROUTES),
  },
  {
    path: 'job/:jobKey/assign-vendor',
    canActivate: [authGuard, requiresFunction('jobs.view')],
    loadComponent: () =>
      import('./features/job/assign-vendor/assign-vendor.component').then(
        (m) => m.AssignVendorComponent
      ),
    title: 'Assign Vendor',
  },
  {
    path: 'job/:jobKey/estimates',
    canActivate: [authGuard, requiresFunction('jobs.view')],
    loadComponent: () =>
      import('./features/job/estimates/estimates.component').then(
        (m) => m.EstimatesComponent
      ),
    title: 'Estimates',
  },
  {
    path: 'job/:jobKey/notes-activity',
    canActivate: [authGuard, requiresFunction('jobs.view')],
    loadComponent: () =>
      import('./features/job/notes-activity/notes-activity.component').then(
        (m) => m.NotesActivityComponent
      ),
    title: 'Notes & Activity',
  },
  {
    path: 'job/:jobKey/vendor-bills',
    canActivate: [authGuard, requiresFunction('jobs.view')],
    loadComponent: () =>
      import('./features/job/vendor-bills/vendor-bills.component').then(
        (m) => m.VendorBillsComponent
      ),
    title: 'Customer & Vendor Invoicing',
  },
  {
    path: 'accounting/move-to-accounting',
    canActivate: [authGuard, requiresFunction('accounting.view')],
    loadComponent: () =>
      import('./features/accounting/move-to-accounting/move-to-accounting.component').then(
        (m) => m.MoveToAccountingComponent
      ),
    title: 'Move to Accounting',
  },
  {
    path: 'accounting/sent-back-to-service',
    canActivate: [authGuard, requiresFunction('accounting.view')],
    loadComponent: () =>
      import('./features/accounting/sent-back-to-service/sent-back-to-service.component').then(
        (m) => m.SentBackToServiceComponent
      ),
    title: 'Sent Back to Service',
  },
  {
    path: 'accounting/markup-override-approval',
    canActivate: [authGuard, requiresFunction('accounting.view')],
    loadComponent: () =>
      import('./features/accounting/markup-override-approval/markup-override-approval.component').then(
        (m) => m.MarkupOverrideApprovalComponent
      ),
    title: 'Below Minimum Mark-up Approvals',
  },
  {
    path: 'reports/customer-gross-profit',
    canActivate: [customerGrossProfitAccessGuard, requiresFunction('reports.view')],
    loadComponent: () =>
      import('./features/reports/customer-gross-profit/customer-gross-profit.component').then(
        (m) => m.CustomerGrossProfitComponent
      ),
    title: 'Customer Gross Profit',
  },
  {
    // Public: linked from the legacy sign-in page ("Forgot password?"). The caller has no JWT by
    // definition, so no authGuard; identity is proven by the emailed one-time code.
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/account/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent
      ),
    title: 'Forgot password',
  },
  {
    path: 'distant-vendor-approval/approve/:approvalKey',
    loadComponent: () =>
      import('./features/distant-vendor/distant-vendor-approve.component').then(
        (m) => m.DistantVendorApproveComponent
      ),
    title: 'Approve Distant Vendor',
  },
  {
    path: 'distant-vendor-approval/decline/:approvalKey',
    loadComponent: () =>
      import('./features/distant-vendor/distant-vendor-decline.component').then(
        (m) => m.DistantVendorDeclineComponent
      ),
    title: 'Decline Distant Vendor',
  },
  {
    // Public: the manager's on-site approval override form, opened from the time-sensitive email. No authGuard; the
    // emailed single-use token is the credential.
    path: 'onsite-approval-override/:token',
    loadComponent: () =>
      import('./features/onsite-dne-override/onsite-dne-override-form.component').then(
        (m) => m.OnsiteDneOverrideFormComponent
      ),
    title: 'On-site Approval Override',
  },
  {
    path: 'settings',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/settings.component').then(
        (m) => m.SettingsComponent
      ),
    title: 'Settings',
  },
  {
    path: 'settings/vendor-contract',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-contract/vendor-contract.component').then(
        (m) => m.VendorContractComponent
      ),
    title: 'Vendor Contract Setup',
  },
  {
    path: 'settings/vendor-resubmit-reasons',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-resubmit-reasons/vendor-resubmit-reasons.component').then(
        (m) => m.VendorResubmitReasonsComponent
      ),
    title: 'Vendor Resubmit Reasons',
  },
  {
    path: 'settings/common-broadcast-radius',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/common-broadcast-radius/common-broadcast-radius.component').then(
        (m) => m.CommonBroadcastRadiusComponent
      ),
    title: 'Common Broadcast Radius',
  },
  {
    path: 'settings/rule-threshold-customer-invoice',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/rule-threshold-customer-invoice/rule-threshold-customer-invoice.component').then(
        (m) => m.RuleThresholdCustomerInvoiceComponent
      ),
    title: 'Rule Threshold (Customer Invoice)',
  },
  {
    path: 'settings/vendor-mobile-app',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-mobile-app-setting/vendor-mobile-app-setting.component').then(
        (m) => m.VendorMobileAppSettingComponent
      ),
    title: 'Vendor Mobile App',
  },
  {
    path: 'settings/vendor-email-config',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-email-config/vendor-email-config.component').then(
        (m) => m.VendorEmailConfigComponent
      ),
    title: 'Vendor Email Configuration',
  },
  {
    path: 'settings/vendor-email-templates',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-email-templates/vendor-email-templates.component').then(
        (m) => m.VendorEmailTemplatesComponent
      ),
    title: 'Vendor Email Templates',
  },
  {
    path: 'settings/vendor-charge-type',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-charge-type/vendor-charge-type.component').then(
        (m) => m.VendorChargeTypeComponent
      ),
    title: 'Vendor Charge Type',
  },
  {
    path: 'settings/vendor-grid-setup',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-grid-setup/vendor-grid-setup.component').then(
        (m) => m.VendorGridSetupComponent
      ),
    title: 'Vendor Grid Setup',
  },
  {
    path: 'settings/vendor-profile-emails',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-profile-emails/vendor-profile-emails.component').then(
        (m) => m.VendorProfileEmailsComponent
      ),
    title: 'Profile Completion Emails',
  },
  {
    path: 'settings/vendor-payment-status',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/vendor-payment-status/vendor-payment-status.component').then(
        (m) => m.VendorPaymentStatusComponent
      ),
    title: 'Vendor Payment Status',
  },
  {
    path: 'settings/customer-bulk-email',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/customer-bulk-email/customer-bulk-email.component').then(
        (m) => m.CustomerBulkEmailComponent
      ),
    title: 'Send Bulk Email to Customer',
  },
  {
    path: 'settings/invoice-approving-agent',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/invoice-approving-agent/invoice-approving-agent.component').then(
        (m) => m.InvoiceApprovingAgentComponent
      ),
    title: 'Invoice Approving Agent',
  },
  {
    path: 'settings/customer-terms',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/customer-terms/customer-terms.component').then(
        (m) => m.CustomerTermsComponent
      ),
    title: 'Terms and Conditions',
  },
  {
    path: 'settings/trade-charge-template',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/trade-charge-template/trade-charge-template.component').then(
        (m) => m.TradeChargeTemplateComponent
      ),
    title: 'Trade Charge Type Template',
  },
  {
    path: 'settings/customer-email-templates',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/customer-email-templates/customer-email-templates.component').then(
        (m) => m.CustomerEmailTemplatesComponent
      ),
    title: 'Customer Email Templates',
  },
  {
    path: 'settings/customer-job-status',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/customer-job-status/customer-job-status.component').then(
        (m) => m.CustomerJobStatusComponent
      ),
    title: 'Customer Job Status',
  },
  {
    path: 'settings/customer-email-config',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/customer-email-config/customer-email-config.component').then(
        (m) => m.CustomerEmailConfigComponent
      ),
    title: 'Customer Email Configuration',
  },
  {
    path: 'settings/job-request-email',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/job-request-email/job-request-email.component').then(
        (m) => m.JobRequestEmailComponent
      ),
    title: 'Job Request Email Configuration',
  },
  {
    path: 'settings/estimate-prep-manager',
    canActivate: [authGuard, settingsPageView],
    data: { type: 'estimate' },
    loadComponent: () =>
      import('./features/settings/prep-manager-email/prep-manager-email.component').then(
        (m) => m.PrepManagerEmailComponent
      ),
    title: 'Estimate Prep Manager Email',
  },
  {
    path: 'settings/invoice-prep-manager',
    canActivate: [authGuard, settingsPageView],
    data: { type: 'invoice' },
    loadComponent: () =>
      import('./features/settings/prep-manager-email/prep-manager-email.component').then(
        (m) => m.PrepManagerEmailComponent
      ),
    title: 'Invoice Prep Manager Email',
  },
  {
    path: 'settings/customer-region',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/customer-region/customer-region.component').then(
        (m) => m.CustomerRegionComponent
      ),
    title: 'Region',
  },
  {
    path: 'settings/common-email-config',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/common-email-config/common-email-config.component').then(
        (m) => m.CommonEmailConfigComponent
      ),
    title: 'Common Email Config',
  },
  {
    path: 'settings/admin-email-templates',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/admin-email-templates/admin-email-templates.component').then(
        (m) => m.AdminEmailTemplatesComponent
      ),
    title: 'Email Template for Admin',
  },
  {
    path: 'settings/admin-email-config',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/admin-email-config/admin-email-config.component').then(
        (m) => m.AdminEmailConfigComponent
      ),
    title: 'Admin Email Configuration',
  },
  {
    path: 'settings/email-receivers',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/email-receivers/email-receivers.component').then(
        (m) => m.EmailReceiversComponent
      ),
    title: 'Email Receivers',
  },
  {
    path: 'settings/notes-to-accounting',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/notes-to-accounting/notes-to-accounting.component').then((m) => m.NotesToAccountingComponent),
    title: 'Notes to Accounting',
  },
  {
    path: 'settings/document-setup',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/document-setup/document-setup.component').then((m) => m.DocumentSetupComponent),
    title: 'Document Setup',
  },
  {
    path: 'settings/document-type',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/document-type/document-type.component').then((m) => m.DocumentTypeComponent),
    title: 'Document Type',
  },
  {
    path: 'settings/job-priority',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/job-priority/job-priority.component').then((m) => m.JobPriorityComponent),
    title: 'Job Priority',
  },
  {
    path: 'settings/job-status',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/job-status/job-status.component').then((m) => m.JobStatusComponent),
    title: 'Job Status',
  },
  {
    path: 'settings/country',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/country/country.component').then((m) => m.CountryComponent),
    title: 'Country',
  },
  {
    path: 'settings/gis',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/gis/gis.component').then((m) => m.GisComponent),
    title: 'GIS',
  },
  {
    path: 'settings/common-region',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/common-region/common-region.component').then((m) => m.CommonRegionComponent),
    title: 'Common Region',
  },
  {
    path: 'settings/company-info',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/company-info/company-info.component').then((m) => m.CompanyInfoComponent),
    title: 'Company Info',
  },
  {
    path: 'settings/assign-priority',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/assign-priority/assign-priority.component').then((m) => m.AssignPriorityComponent),
    title: 'Assign Priority to User',
  },
  {
    path: 'settings/dashboard-grid-setup',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/dashboard-grid-setup/dashboard-grid-setup.component').then((m) => m.DashboardGridSetupComponent),
    title: 'Dashboard Grid Setup',
  },
  {
    path: 'settings/dashboard-grid-usergroups',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/dashboard-grid-usergroups/dashboard-grid-usergroups.component').then((m) => m.DashboardGridUsergroupsComponent),
    title: 'Dashboard Grid Usergroups',
  },
  {
    path: 'settings/manage-grid-title',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () =>
      import('./features/settings/manage-grid-title/manage-grid-title.component').then((m) => m.ManageGridTitleComponent),
    title: 'Manage Grid Title',
  },
  {
    path: 'settings/common-notes',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/common-notes/common-notes.component').then((m) => m.CommonNotesComponent),
    title: 'Common Notes',
  },
  {
    path: 'settings/note-footer',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/note-footer/note-footer.component').then((m) => m.NoteFooterComponent),
    title: 'Note Footer',
  },
  {
    path: 'settings/vendor-master-rate',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/vendor-master-rate/vendor-master-rate.component').then((m) => m.VendorMasterRateComponent),
    title: 'Vendor Master Rate',
  },
  {
    path: 'settings/trade-setup',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/trade-setup/trade-setup.component').then((m) => m.TradeSetupComponent),
    title: 'Trade Setup',
  },
  {
    path: 'settings/accounting-status',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/accounting-status/accounting-status.component').then((m) => m.AccountingStatusComponent),
    title: 'Accounting Status Setup',
  },
  {
    path: 'settings/team-setup',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/team-setup/team-setup.component').then((m) => m.TeamSetupComponent),
    title: 'Team Setup',
  },
  {
    path: 'settings/user-groups',
    canActivate: [authGuard, requiresFunction('usergroups.manage')],
    loadComponent: () => import('./features/settings/user-groups/user-groups.component').then((m) => m.UserGroupsComponent),
    title: 'User Groups',
  },
  {
    path: 'settings/sales-charge-type',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/sales-charge-type/sales-charge-type.component').then((m) => m.SalesChargeTypeComponent),
    title: 'Sales Charge Type',
  },
  {
    path: 'settings/time-definition',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/time-definition/time-definition.component').then((m) => m.TimeDefinitionComponent),
    title: 'Time Definition',
  },
  {
    path: 'settings/distance-rule',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/distance-rule/distance-rule.component').then((m) => m.DistanceRuleComponent),
    title: 'Distance Rule Management',
  },
  {
    path: 'settings/account-manager-feedback',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/account-manager-feedback/account-manager-feedback.component').then((m) => m.AccountManagerFeedbackComponent),
    title: 'Account Manager Feedback Setup',
  },
  {
    path: 'settings/store-manager-survey',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/store-manager-survey/store-manager-survey.component').then((m) => m.StoreManagerSurveyComponent),
    title: 'Store Manager Survey Setup',
  },
  {
    path: 'settings/vendor-question-setup',
    canActivate: [authGuard, settingsPageView],
    loadComponent: () => import('./features/settings/vendor-question-setup/vendor-question-setup.component').then((m) => m.VendorQuestionSetupComponent),
    title: 'Vendor Registration Question Setup',
  },
  {
    // Legacy-style URL kept intentionally (mirrors V1 /Staff/Index). Settings > User Management > Staff.
    path: 'Staff/Index',
    canActivate: [authGuard, requiresFunction('staff.view')],
    loadComponent: () =>
      import('./features/staff/staff-list/staff-list.component').then((m) => m.StaffListComponent),
    title: 'Staff',
  },
  {
    path: 'Staff/Create',
    canActivate: [authGuard, requiresFunction('staff.view')],
    loadComponent: () =>
      import('./features/staff/staff-form/staff-form.component').then((m) => m.StaffFormComponent),
    title: 'Add New Staff',
  },
  {
    // Legacy-style URL kept intentionally (APV-14 targets /MgtLocation/Index & /MgtLocation/Create).
    path: 'MgtLocation/Index',
    canActivate: [authGuard, requiresFunction('locations.view')],
    loadComponent: () =>
      import('./features/locations/location-list/location-list.component').then(
        (m) => m.LocationListComponent
      ),
    title: 'Locations',
  },
  {
    path: 'MgtLocation/Create',
    canActivate: [authGuard, requiresFunction('locations.view')],
    loadComponent: () =>
      import('./features/locations/location-form/location-form.component').then(
        (m) => m.LocationFormComponent
      ),
    title: 'Add New Location',
  },
  {
    // Same dual-mode form component as Create; the :id param puts it in Edit mode.
    path: 'MgtLocation/Edit/:id',
    canActivate: [authGuard, requiresFunction('locations.view')],
    loadComponent: () =>
      import('./features/locations/location-form/location-form.component').then(
        (m) => m.LocationFormComponent
      ),
    title: 'Edit Location',
  },
  {
    path: 'MgtLocation/Details/:id',
    canActivate: [authGuard, requiresFunction('locations.view')],
    loadComponent: () =>
      import('./features/locations/location-detail/location-detail.component').then(
        (m) => m.LocationDetailComponent
      ),
    title: 'Location Details',
  },
  {
    path: 'customers',
    canActivate: [authGuard, requiresFunction('customers.view')],
    loadComponent: () =>
      import('./features/customers/customer-list/customer-list.component').then(
        (m) => m.CustomerListComponent
      ),
    title: 'Customers',
  },
  {
    path: 'customers/new',
    canActivate: [authGuard, requiresFunction('customers.view')],
    loadComponent: () =>
      import('./features/customers/customer-create/customer-create.component').then(
        (m) => m.CustomerCreateComponent
      ),
    title: 'Add New Customer',
  },
  {
    path: 'customers/:id/edit',
    canActivate: [authGuard, requiresFunction('customers.view')],
    loadComponent: () =>
      import('./features/customers/customer-edit/customer-edit.component').then(
        (m) => m.CustomerEditComponent
      ),
    title: 'Edit Customer',
  },
  {
    path: 'customers/:id',
    canActivate: [authGuard, requiresFunction('customers.view')],
    loadComponent: () =>
      import('./features/customers/customer-detail/customer-detail.component').then(
        (m) => m.CustomerDetailComponent
      ),
    title: 'Customer Details',
  },
  {
    path: 'vendors',
    canActivate: [authGuard, requiresFunction('vendors.view')],
    loadComponent: () =>
      import('./features/vendors/vendor-list/vendor-list.component').then(
        (m) => m.VendorListComponent
      ),
    title: 'Vendors',
  },
  {
    path: 'vendors/new',
    canActivate: [authGuard, requiresFunction('vendors.view')],
    loadComponent: () =>
      import('./features/vendors/vendor-create/vendor-create.component').then(
        (m) => m.VendorCreateComponent
      ),
    title: 'Add New Vendor',
  },
  {
    path: 'vendors/:id/edit',
    canActivate: [authGuard, requiresFunction('vendors.view')],
    loadComponent: () =>
      import('./features/vendors/vendor-edit/vendor-edit.component').then(
        (m) => m.VendorEditComponent
      ),
    title: 'Edit Vendor',
  },
  {
    path: 'vendors/:id/bills/:billKey/preview',
    canActivate: [authGuard, requiresFunction('vendors.view')],
    loadComponent: () =>
      import('./features/vendors/vendor-bill-preview/vendor-bill-preview.component').then(
        (m) => m.VendorBillPreviewComponent
      ),
    title: 'Bill Preview',
  },
  {
    // Chat pop-up only, shown by Admin Portal V1 in a frame (V1 AdminPortalV2/ChatEmbed hands over the
    // token). No authGuard: its login redirect would load inside the small frame; the pop-up asks V1
    // for a fresh token instead (LiveChatEmbedService).
    path: 'embed/chat',
    loadComponent: () =>
      import('./features/live-chat/embed-chat-page.component').then((m) => m.EmbedChatPageComponent),
    title: 'Job chat',
  },
  {
    // The chat pop-up in its own browser window (the "Pop out" button): same sign-in as the main window (the token is in
    // localStorage), and the pop-up the app shell renders fills the window. Opens straight into a thread with ?job=&vendor=.
    path: 'chat-window',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/live-chat/embed-chat-page.component').then((m) => m.EmbedChatPageComponent),
    title: 'Job chat',
  },
  {
    path: '',
    loadComponent: () =>
      import('./features/home/home.component').then((m) => m.HomeComponent),
    pathMatch: 'full',
    title: 'Home',
  },
];
